import React, { useState } from 'react';
import { X, Check, Clock, MapPin, User, CreditCard, Train, ArrowRight, Users } from 'lucide-react';
import { getStationName } from '../utils/stations';
import { formatDateTime } from '../utils/dateUtils';
import { toast } from 'react-hot-toast';
import MD5 from 'crypto-js/md5';

const BookingConfirmation = ({ booking, onClose, onCancel, t }) => {
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelSuccess, setCancelSuccess] = useState(false);

  if (!booking) return null;

  const ticket = booking.tickets?.[0] || booking;
  
  // Получаем данные о пассажире из разных возможных источников
  const passengerName = (() => {
    // Проверяем данные в разных местах
    const firstName = booking.firstName || 
                     ticket.firstName || 
                     booking.passengersInfo?.passengers?.[0]?.firstName ||
                     ticket.passengersInfo?.passengers?.[0]?.firstName;
    
    // Проверяем все возможные источники фамилии
    const lastName = booking.surname || 
                    booking.lastName || 
                    ticket.surname ||
                    ticket.lastName || 
                    booking.passengersInfo?.passengers?.[0]?.surname ||
                    booking.passengersInfo?.passengers?.[0]?.lastName ||
                    ticket.passengersInfo?.passengers?.[0]?.surname ||
                    ticket.passengersInfo?.passengers?.[0]?.lastName;

    // Добавляем подробный отладочный вывод
    console.log('Passenger name resolution:', { 
      firstName, 
      lastName,
      sources: {
        booking: {
          firstName: booking.firstName,
          lastName: booking.lastName,
          surname: booking.surname,
          passengerInfo: booking.passengersInfo?.passengers?.[0]
        },
        ticket: {
          firstName: ticket.firstName,
          lastName: ticket.lastName,
          surname: ticket.surname,
          passengerInfo: ticket.passengersInfo?.passengers?.[0]
        }
      }
    });

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    }
    return 'Не указано';
  })();

  // Получаем данные о станциях с полными названиями
  const fromStation = (() => {
    const station = ticket.departureStationName || 
                   ticket.departureStation || 
                   booking.departureCityName ||
                   'Не указано';
    // Преобразуем сокращенные названия
    if (station === 'MS') return 'Брно';
    if (station === 'Sta.') return 'Бржецлав';
    return station;
  })();

  const toStation = (() => {
    const station = ticket.arrivalStationName || 
                   ticket.arrivalStation || 
                   booking.arrivalCityName ||
                   'Не указано';
    // Преобразуем сокращенные названия
    if (station === 'MS') return 'Брно';
    if (station === 'Sta.') return 'Бржецлав';
    return station;
  })();

  // Получаем данные о месте
  const seatNumber = ticket.seatNumber || 
                    booking.seatNumbers?.[0] || 
                    'Не указано';
  
  const vehicleNumber = ticket.vehicleNumber || 
                       booking.vehicleNumbers?.[0] || 
                       'Не указано';

  // Добавляем отображение стоимости
  const price = booking.price || ticket.price || 0;
  const currency = (booking.currency || ticket.currency || 'EUR').toUpperCase();

  // Обновляем функцию форматирования времени в пути
  const formatTravelTime = (travelTime) => {
    if (!travelTime) return '';
    
    // Сначала убираем все пробелы вокруг 'h'
    let formatted = travelTime.replace(/\s*h\s*/g, 'h');
    // Затем убираем дублирование 'h'
    formatted = formatted.replace(/h+/g, 'h');
    // Добавляем пробел перед 'h'
    formatted = formatted.replace(/h/, ' h');
    // Убираем лишние пробелы
    return formatted.trim();
  };

  // Получаем время в пути
  const travelTime = formatTravelTime(ticket.travelTime || booking.travelTime);

  // Форматирование времени
  const formatTime = (dateString) => {
    try {
      return new Date(dateString).toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return '';
    }
  };

  // Форматирование даты
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Получаем email пассажира из разных возможных источников
  const passengerEmail = booking.email || 
                        ticket.email ||
                        booking.passengersInfo?.passengers?.[0]?.email ||
                        ticket.passengersInfo?.passengers?.[0]?.email;

  // Печать билета
  const handlePrintTicket = async () => {
    try {
      const cleanToken = booking.token.trim().replace(/^Bearer\s+/i, '');
      const authToken = `Bearer ${cleanToken}`;

      console.log('Printing ticket:', {
        id: ticket.id,
        accountCode: ticket.accountCode,
        tokenPreview: cleanToken.substring(0, 20) + '...'
      });

      const response = await fetch(
        `/api/tickets/${ticket.accountCode}/${ticket.id}/print`,
        {
          headers: {
            'Authorization': authToken,
            'Accept': 'text/html'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to get ticket print data');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
          setTimeout(() => {
            window.URL.revokeObjectURL(url);
          }, 100);
        };
      } else {
        throw new Error('Popup blocked. Please allow popups and try again.');
      }
    } catch (error) {
      console.error('Print error:', error);
      alert(t.printError || 'Failed to print ticket');
    }
  };

  // Показать QR код
  const handleShowQR = async () => {
    try {
      // Поддержка старого и нового формата
      if (ticket.accountCode) {
        // Старый формат
        const cleanToken = booking.token.trim().replace(/^Bearer\s+/i, '');
        const authToken = `Bearer ${cleanToken}`;

        console.log('Getting QR code (legacy):', {
          id: ticket.id,
          accountCode: ticket.accountCode,
          tokenPreview: cleanToken.substring(0, 20) + '...'
        });

        const response = await fetch(
          `/api/tickets/${ticket.accountCode}/${ticket.id}/qrcode/png`,
          {
            headers: {
              'Authorization': authToken,
              'Accept': 'image/png'
            }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to get QR code');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        window.open(url, '_blank');
      } else {
        // Новый формат с полными данными билета
        const ticketData = {
          ticketId: ticket.id,
          passenger: {
            firstName: booking.firstName || ticket.firstName,
            lastName: booking.lastName || ticket.lastName || booking.surname || ticket.surname,
          },
          route: {
            from: fromStation,
            to: toStation,
            departureTime: ticket.departureTime,
            arrivalTime: ticket.arrivalTime,
            travelTime: travelTime
          },
          seat: {
            number: seatNumber,
            vehicleNumber: vehicleNumber
          },
          price: {
            amount: price,
            currency: currency
          }
        };

        const ticketDataString = JSON.stringify(ticketData);
        const qrDataUrl = `/api/tickets/qr?data=${encodeURIComponent(ticketDataString)}`;

        const response = await fetch(qrDataUrl, {
          headers: {
            'Accept': 'image/png'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to get QR code');
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        const qrWindow = window.open('', '_blank');
        if (qrWindow) {
          qrWindow.document.write(`
            <html>
              <head>
                <title>Билет #${ticket.id}</title>
                <style>
                  body {
                    font-family: Arial, sans-serif;
                    margin: 20px;
                    text-align: center;
                  }
                  .ticket-info {
                    margin: 20px auto;
                    max-width: 600px;
                    text-align: left;
                  }
                  .qr-code {
                    margin: 20px auto;
                  }
                  .qr-code img {
                    max-width: 300px;
                  }
                </style>
              </head>
              <body>
                <h1>Билет #${ticket.id}</h1>
                <div class="ticket-info">
                  <h2>Пассажир</h2>
                  <p>${passengerName}</p>
                  <h2>Маршрут</h2>
                  <p>Откуда: ${fromStation}</p>
                  <p>Куда: ${toStation}</p>
                  <p>Отправление: ${formatDateTime(ticket.departureTime)}</p>
                  <p>Прибытие: ${formatDateTime(ticket.arrivalTime)}</p>
                  ${travelTime ? `<p>Время в пути: ${travelTime}</p>` : ''}
                  <h2>Место</h2>
                  <p>Место: ${seatNumber}</p>
                  <p>Вагон №: ${vehicleNumber}</p>
                  <h2>Стоимость</h2>
                  <p>${price} ${currency}</p>
                </div>
                <div class="qr-code">
                  <img src="${url}" alt="QR Code" />
                </div>
              </body>
            </html>
          `);
          qrWindow.document.close();
        }
      }
    } catch (error) {
      console.error('QR error:', error);
      alert(t.qrError || 'Failed to show QR code');
    }
  };

  // Отмена билета
  const handleCancelTicket = async () => {
    try {
      setCancelling(true);
      setCancelError(null);

      // Получаем данные из объекта бронирования
      const ticketId = ticket.id || booking.id || booking.booking_id;
      const token = booking.token || booking.booking_token;
      
      // Важно! Используем accountCode из бронирования, даже если он в разных форматах
      let accountCode = booking.accountCode || booking.account_code;

      console.log('Starting ticket cancellation with data:', {
        ticketId: ticketId,
        tokenPreview: token ? token.substring(0, 10) + '...' : 'missing',
        accountCode: accountCode || 'not found in booking'
      });

      // Проверяем наличие необходимых данных
      if (!ticketId || !token) {
        console.error('Insufficient ticket data for cancellation:', {
          ticketId,
          hasToken: !!token
        });
        throw new Error(t.insufficientCancellationData || 'Недостаточно данных для отмены билета');
      }

      // Получаем токен авторизации
      const cleanToken = token.replace(/^Bearer\s+/i, '').trim();
      
      // Если accountCode еще не установлен, пытаемся получить его через authenticate
      if (!accountCode) {
        try {
          console.log('Account code not found in booking data, authenticating token...');
          
          // Проверяем наличие accountCode в базе данных
          try {
            console.log(`Checking database for account code for booking ${ticketId}`);
            const bookingResponse = await fetch(`/api/bookings/${ticketId}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json'
              }
            });
            
            if (bookingResponse.ok) {
              const bookingData = await bookingResponse.json();
              if (bookingData.booking && bookingData.booking.account_code) {
                accountCode = bookingData.booking.account_code;
                console.log(`Found account code ${accountCode} in database for booking ${ticketId}`);
              }
            }
          } catch (dbError) {
            console.error('Failed to check account code in database:', dbError);
          }
          
          // Если accountCode не найден в базе данных, пытаемся получить его через аутентификацию
          if (!accountCode) {
            // Устанавливаем таймаут для запроса аутентификации
            const authPromise = fetch('/api/tickets/users/authenticate', {
              method: 'GET',
              headers: {
                'Authorization': cleanToken,
                'X-Auth-Token': cleanToken,
                'X-Application-Origin': 'AFF',
                'X-Lang': 'ru'
              }
            });
            
            // Создаем таймаут в 10 секунд
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Таймаут аутентификации')), 10000)
            );
            
            // Используем Promise.race для предотвращения зависания
            const authResponse = await Promise.race([authPromise, timeoutPromise]);
            
            if (!authResponse.ok) {
              throw new Error(`Ошибка аутентификации: ${authResponse.status}`);
            }
            
            const authData = await authResponse.json();
            
            if (!authData.accountCode) {
              throw new Error('Не удалось получить код аккаунта');
            }
            
            accountCode = authData.accountCode;
            console.log('Authentication successful, received account code:', accountCode);
            
            // Сохраняем accountCode в базу данных
            try {
              await fetch('/api/bookings/update-status', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  booking_id: ticketId,
                  account_code: accountCode
                })
              });
              console.log(`Account code ${accountCode} saved to database for booking ${ticketId}`);
            } catch (dbError) {
              console.error('Failed to update account code in database:', dbError);
            }
          }
        } catch (authError) {
          console.error('Authentication failed:', authError);
          
          // Если все равно нет accountCode, используем запасной вариант
          if (!accountCode) {
            accountCode = "unknown";
            console.warn('Using "unknown" as account code for cancellation');
          }
        }
      } else {
        console.log(`Using account code ${accountCode} from booking data`);
      }
      
      // Получаем информацию о билете для получения точного controlHash
      let controlHash = null;
      let maxAttempts = 3;
      let currentAttempt = 0;
      let success = false;
      let lastError = null;
      
      while (currentAttempt < maxAttempts && !success) {
        currentAttempt++;
        console.log(`Attempt ${currentAttempt}/${maxAttempts} to cancel ticket ${ticketId} with account code ${accountCode}`);
        
        try {
          // Получение точного controlHash на каждой попытке
          try {
            console.log(`Fetching ticket info from /api/regiojet/tickets/${accountCode}/${ticketId}`);
            
            // Пробуем сначала получить информацию через новый эндпоинт
            const ticketInfoResponse = await fetch(`/api/tickets/${accountCode}/${ticketId}/info`, {
              method: 'GET',
              headers: {
                'Authorization': cleanToken,
                'X-Auth-Token': cleanToken,
                'X-Application-Origin': 'AFF',
                'X-Lang': 'ru'
              }
            });
            
            if (ticketInfoResponse.ok) {
              const ticketInfo = await ticketInfoResponse.json();
              console.log('Ticket info response:', ticketInfo);
              
              if (ticketInfo && ticketInfo.controlHash) {
                controlHash = ticketInfo.controlHash;
                console.log('Got control hash from API:', controlHash.substring(0, 10) + '...');
              } else {
                console.warn('Ticket info response does not contain controlHash, trying to extract from HMAC or cancelHash');
                
                // Пытаемся найти другие поля, которые могут содержать хеш
                if (ticketInfo.hmac) {
                  controlHash = ticketInfo.hmac;
                  console.log('Using HMAC as control hash:', controlHash.substring(0, 10) + '...');
                } else if (ticketInfo.cancelHash) {
                  controlHash = ticketInfo.cancelHash;
                  console.log('Using cancelHash as control hash:', controlHash.substring(0, 10) + '...');
                } else if (ticketInfo.id) {
                  // Если есть ID билета, но нет других хешей, генерируем хеш на основе его данных
                  const dataForHash = `${ticketInfo.id}-${ticketInfo.departureTime}`;
                  controlHash = MD5(dataForHash).toString().toUpperCase();
                  console.log('Generated control hash from ticket data:', controlHash.substring(0, 10) + '...');
                } else {
                  console.warn('No useful hash data found in API response');
                }
              }
            } else {
              // Если не удалось получить информацию о билете через новый эндпоинт, 
              // пробуем через старый
              console.log('Fallback to regiojet API endpoint');
              
              const regiojetResponse = await fetch(`/api/regiojet/tickets/${accountCode}/${ticketId}`, {
                method: 'GET',
                headers: {
                  'Authorization': cleanToken,
                  'X-Auth-Token': cleanToken,
                  'X-Application-Origin': 'AFF',
                  'X-Lang': 'ru'
                }
              });
              
              if (regiojetResponse.ok) {
                const regiojetInfo = await regiojetResponse.json();
                console.log('Regiojet API response:', regiojetInfo);
                
                if (regiojetInfo && regiojetInfo.controlHash) {
                  controlHash = regiojetInfo.controlHash;
                  console.log('Got control hash from Regiojet API:', controlHash.substring(0, 10) + '...');
                } else if (regiojetInfo.hmac) {
                  controlHash = regiojetInfo.hmac;
                  console.log('Using HMAC from Regiojet API:', controlHash.substring(0, 10) + '...');
                } else if (regiojetInfo.cancelHash) {
                  controlHash = regiojetInfo.cancelHash;
                  console.log('Using cancelHash from Regiojet API:', controlHash.substring(0, 10) + '...');
                }
              } else {
                // Если не удалось получить информацию о билете
                const errorData = await regiojetResponse.json().catch(() => ({}));
                console.error('Failed to get ticket info from Regiojet API:', errorData);
                
                if (regiojetResponse.status === 401 || regiojetResponse.status === 403) {
                  throw new Error('Authentication failed when getting ticket info');
                }
              }
            }
          } catch (ticketInfoError) {
            console.error('Error fetching ticket info:', ticketInfoError);
          }
          
          // Если не удалось получить controlHash, генерируем свой
          if (!controlHash) {
            controlHash = generateControlHash(ticketId);
            console.log('Using generated control hash:', controlHash.substring(0, 10) + '...');
          }

          // Отправляем запрос на отмену
          console.log('Sending cancellation request:', {
            url: `/api/regiojet/tickets/${accountCode}/${ticketId}/cancel`,
            controlHash: controlHash.substring(0, 10) + '...'
          });

          const response = await fetch(`/api/regiojet/tickets/${accountCode}/${ticketId}/cancel`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': cleanToken,
              'X-Auth-Token': cleanToken,
              'X-Application-Origin': 'AFF',
              'X-Lang': 'ru'
            },
            body: JSON.stringify({
              controlHash: controlHash,
              refundToOriginalSource: true
            })
          });

          // Обрабатываем ответ
          if (response.ok) {
            const data = await response.json();
            console.log('Cancel successful:', data);
            success = true;
            break;
          }
          
          const errorData = await response.json();
          console.error('Cancel API error:', {
            status: response.status,
            response: errorData
          });
          
          lastError = errorData;
          
          // Если ошибка "Change of data", попробуем еще раз с новым controlHash
          if (response.status === 400 && errorData.message && errorData.message.includes('Change of data')) {
            console.log('Data changed, will retry with new controlHash');
            
            // Сбрасываем controlHash, он будет получен заново на следующей итерации
            controlHash = null;
            
            // Пауза перед следующей попыткой
            if (currentAttempt < maxAttempts) {
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            }
          }
          
          // Если ошибка аутентификации или другая ошибка запроса, пробуем запасной метод
          if (response.status === 400 || response.status === 403 || response.status === 401) {
            console.log('Using fallback cancellation method...');
            
            const fallbackResponse = await fetch('/api/tickets/cancel', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': cleanToken,
                'X-Auth-Token': cleanToken
              },
              body: JSON.stringify({
                ticketId: ticketId,
                token: cleanToken,
                controlHash: controlHash,
                bookingData: {
                  email: booking.email,
                  firstName: booking.firstName || booking.first_name,
                  lastName: booking.lastName || booking.last_name,
                  accountCode: accountCode
                }
              })
            });
            
            if (fallbackResponse.ok) {
              const fallbackData = await fallbackResponse.json();
              console.log('Fallback cancellation successful:', fallbackData);
              success = true;
              break;
            } else {
              const fallbackError = await fallbackResponse.json().catch(() => ({}));
              
              console.error('Fallback cancellation error:', fallbackError);
              lastError = fallbackError;
              
              // Если ошибка "Change of data" в запасном методе, попробуем еще раз
              if (fallbackError.message && fallbackError.message.includes('Change of data')) {
                console.log('Data changed in fallback method, will retry');
                
                // Сбрасываем controlHash
                controlHash = null;
                
                if (currentAttempt < maxAttempts) {
                  await new Promise(resolve => setTimeout(resolve, 2000));
                  continue;
                }
              }
              
              // Если достигли максимального числа попыток, выходим с ошибкой
              if (currentAttempt >= maxAttempts) {
                throw new Error(fallbackError.message || errorData.message || t.cancelError || 'Ошибка при отмене билета');
              }
            }
          } else {
            // Другие ошибки
            throw new Error(errorData.message || t.cancelError || 'Ошибка при отмене билета');
          }
        } catch (cancelError) {
          console.error('Error during cancellation attempt:', cancelError);
          
          if (currentAttempt < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          } else {
            throw cancelError;
          }
        }
      }
      
      if (!success) {
        throw new Error(lastError?.message || t.cancelFailure || 'Не удалось отменить билет после нескольких попыток');
      }

      // Обновляем состояние
      setCancelSuccess(true);
      
      // Обновляем состояние билета в базе данных
      try {
        await fetch('/api/bookings/update-status', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            booking_id: ticketId,
            status: 'CANCELLED',
            payment_intent_id: booking.payment_intent_id || booking.paymentIntentId
          })
        });
      } catch (updateError) {
        console.error('Error updating booking status after cancellation:', updateError);
      }
      
      // Обработка возврата средств
      if (booking.paymentIntentId || booking.payment_intent_id) {
        try {
          await handleRefund();
        } catch (refundError) {
          console.error('Refund processing error:', refundError);
        }
      }

      // Показываем уведомление об успешной отмене
      toast.success(t.cancelSuccessful || 'Билет успешно отменен');

    } catch (error) {
      console.error('Cancel error:', error);
      setCancelError(error.message || t.cancelFailure || 'Не удалось отменить билет');
      toast.error(error.message || t.cancelFailure || 'Не удалось отменить билет');
    } finally {
      setCancelling(false);
    }
  };

  // Заменяем функцию generateControlHash на более простую версию для браузера
  const generateControlHash = (ticketId) => {
    const timestamp = Date.now();
    const data = `${ticketId}-${timestamp}`;
    return MD5(data).toString().toUpperCase();
  };

  // Обработка возврата средств
  const handleRefund = async () => {
    try {
      const response = await fetch('/api/payments/refund', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          paymentId: booking.paymentId,
          amount: booking.price * 100, // Stripe работает с центами
          currency: booking.currency.toLowerCase(),
          reason: 'requested_by_customer',
          bookingId: booking.id
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Ошибка при возврате средств');
      }

      console.log('Refund processed:', data);
    } catch (error) {
      console.error('Refund error:', error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
      <div className="min-h-screen px-4 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {t.bookingConfirmation || 'Подтверждение бронирования'}
              </h2>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X size={24} />
              </button>
            </div>

            {/* Показываем уведомление об успешной отмене */}
            {cancelSuccess && (
              <div className="bg-green-100 border-l-4 border-green-500 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Check className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-green-700">
                      Билет успешно отменен
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Показываем ошибку отмены */}
            {cancelError && (
              <div className="bg-red-100 border-l-4 border-red-500 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <X className="h-5 w-5 text-red-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">
                      {cancelError}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Информация о билете */}
            <div className="space-y-6">
              {/* Пассажир */}
              <div className="border-b pb-4">
                <h3 className="font-semibold mb-2">{t.passenger || 'Пассажир'}</h3>
                <p>{passengerName}</p>
              </div>

              {/* Маршрут */}
              <div className="border-b pb-4">
                <h3 className="font-semibold mb-2">{t.route || 'Маршрут'}</h3>
                <div className="space-y-2">
                  <p><span className="text-gray-600">{t.from || 'Откуда'}:</span> {fromStation}</p>
                  <p><span className="text-gray-600">{t.to || 'Куда'}:</span> {toStation}</p>
                  <p><span className="text-gray-600">{t.departure || 'Отправление'}:</span> {formatDateTime(ticket.departureTime)}</p>
                  <p><span className="text-gray-600">{t.arrival || 'Прибытие'}:</span> {formatDateTime(ticket.arrivalTime)}</p>
                  {travelTime && (
                    <div className="flex items-center text-gray-600">
                      <Clock className="text-[#9a55f4] mr-2" size={16} />
                      <span>{travelTime}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Детали места */}
              <div className="border-b pb-4">
                <h3 className="font-semibold mb-2">{t.seatDetails || 'Детали места'}</h3>
                <div className="space-y-2">
                  <p><span className="text-gray-600">{t.seat || 'Место'}:</span> {seatNumber}</p>
                  <p><span className="text-gray-600">{t.vehicle || 'Вагон'} №:</span> {vehicleNumber}</p>
                </div>
              </div>

              {/* Добавляем блок со стоимостью */}
              <div className="border-b pb-4">
                <h3 className="font-semibold mb-2">{t.price || 'Стоимость'}</h3>
                <p>{price} {currency}</p>
              </div>
            </div>

            {/* Кнопки действий */}
            <div className="mt-6 space-y-3">
              {ticket.customerActions?.print && (
                <button
                  onClick={handlePrintTicket}
                  className="w-full py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t.printTicket}
                </button>
              )}

              {ticket.customerActions?.qr && (
                <button
                  onClick={handleShowQR}
                  className="w-full py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t.showQR}
                </button>
              )}

              {!booking.cancelled && (
                <button
                  onClick={handleCancelTicket}
                  disabled={cancelling}
                  className="w-full py-2 px-4 bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50"
                >
                  {cancelling ? t.cancelling : t.cancelBooking}
                </button>
              )}

              <button
                onClick={onClose}
                className="w-full py-2 px-4 bg-[#9a55f4] text-white rounded-md hover:bg-[#8645e0]"
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;